import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { LogOut, Eye, XCircle } from 'lucide-react';

const HERO_BG = "https://customer-assets.emergentagent.com/job_gamma-to-web/artifacts/brgxn5xe_Grok%20Image%202026-02-05%20at%2017.41.51.png";

const SuspendedScreen = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const handleLogout = async () => {
        await logout();
        navigate('/login', { replace: true });
    };

    return (
        <div className="min-h-screen flex flex-col items-center justify-center p-4 relative overflow-hidden">
            {/* Background image */}
            <div 
                className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                style={{ backgroundImage: `url(${HERO_BG})` }}
            />
            {/* Dark overlay for readability */}
            <div className="absolute inset-0 bg-background/70 backdrop-blur-[2px]" />

            {/* Logo */}
            <div className="text-center mb-8 z-10">
                <div className="flex items-center justify-center gap-3 mb-4">
                    <div className="p-3 rounded-lg bg-card border border-primary/30 shadow-gold">
                        <Eye className="w-8 h-8 text-primary" />
                    </div>
                </div>
                <h1 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-2">
                    The Decrypted English Method
                </h1>
            </div>

            {/* Suspended Card */}
            <Card className="w-full max-w-md z-10 bg-card/80 backdrop-blur-sm border-border/50 shadow-elevated">
                <CardHeader className="text-center pb-4">
                    <div className="mx-auto mb-4 p-4 rounded-full bg-destructive/10 w-fit">
                        <XCircle className="w-12 h-12 text-destructive" />
                    </div>
                    <CardTitle className="text-xl">Account Suspended</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                    <p className="text-center text-muted-foreground font-ui text-sm">
                        Your account has been suspended. Please contact support for more information.
                    </p>

                    {user?.email && (
                        <div className="text-center text-sm text-muted-foreground font-ui">
                            <p>Account: <span className="text-foreground">{user.email}</span></p>
                        </div>
                    )}

                    <Button
                        variant="outline"
                        size="lg"
                        className="w-full"
                        onClick={handleLogout}
                        data-testid="suspended-logout-btn"
                    >
                        <LogOut className="w-4 h-4 mr-2" />
                        Sign Out
                    </Button>
                </CardContent>
            </Card>

            {/* Footer text */}
            <p className="mt-8 text-center text-sm text-muted-foreground font-ui z-10">
                &copy; {new Date().getFullYear()} Gringos English. All rights reserved.
            </p>
        </div>
    );
};

const ProtectedRoute = ({ children, requireAdmin = false, allowPending = false }) => {
    const { isAuthenticated, isAdmin, isActive, isPending, loading, checkAuth, user } = useAuth();
    const location = useLocation();
    const [isChecking, setIsChecking] = useState(true);

    useEffect(() => {
        // If user data was passed from AuthCallback, skip the check
        if (location.state?.user) {
            setIsChecking(false);
            return;
        }
        
        // Otherwise verify with server
        const verify = async () => {
            if (!isAuthenticated && !loading) {
                await checkAuth();
            }
            setIsChecking(false);
        };
        
        verify();
    }, [location.state?.user, isAuthenticated, loading, checkAuth]);

    // Show loading while checking auth
    if (loading || isChecking) {
        return (
            <div className="min-h-screen bg-gradient-hero flex items-center justify-center">
                <div className="text-center">
                    <div className="w-12 h-12 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="font-ui text-sm text-muted-foreground">Loading...</p>
                </div>
            </div>
        );
    }

    // Redirect to login if not authenticated
    if (!isAuthenticated) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // Handle pending users
    if (isPending && !isAdmin) {
        // If this route allows pending users, render the children
        if (allowPending) {
            return children;
        }
        // Otherwise redirect to pending page
        return <Navigate to="/pending" replace />;
    }

    // Handle suspended users - show suspended screen with logout option
    if (user?.status === 'suspended') {
        return <SuspendedScreen />;
    }

    // Check admin requirement
    if (requireAdmin && !isAdmin) {
        return <Navigate to="/dashboard" replace />;
    }

    // For regular protected routes, require active status (unless allowPending)
    if (!allowPending && !isActive) {
        return <Navigate to="/pending" replace />;
    }

    return children;
};

export default ProtectedRoute;
